import { ReactNode } from 'react';
import { BrandLogo } from '@components/common/BrandLogo';
import { LoginBackgroundSlideshow } from './LoginBackgroundSlideshow';

/**
 * Shell de las pantallas del flujo de acceso (Claude Design · Login.dc.html):
 * hero fotográfico a la izquierda y tarjeta de formulario a la derecha con
 * marcas de registro en las esquinas.
 *
 * Existe porque el mismo hero estaba copiado literal en `LoginPage.tsx` y en
 * `ForgotPasswordPage.tsx`, y `SetNewPasswordPage.tsx` habría sido la tercera
 * copia. Aquellas dos siguen con su versión propia a propósito: son pantallas
 * ya probadas y migrarlas es un cambio aparte, no parte de arreglar el diseño
 * de "establecer contraseña".
 *
 * En móvil y tablet el hero va DEBAJO del formulario (`order`), igual que en
 * Login: lo primero que se necesita es el campo, no la marca.
 */

/** Estilos del diseño, mismos tokens que `LoginForm.tsx`. */
export const FIELD_LABEL = 'text-xs font-semibold text-ink-850';

export const FIELD_INPUT =
  'block w-full rounded-[10px] border border-[#ececec] bg-white px-4 py-[15px] text-[15px] font-medium leading-6 text-ink-850 ' +
  'placeholder:font-normal placeholder:text-secondary-400 ' +
  'hover:border-secondary-300 ' +
  'focus:border-secondary-400 focus:ring-4 focus:ring-secondary-400/[.14] ' +
  'transition-colors';

export const FIELD_INPUT_ERROR =
  'border-danger-500 hover:border-danger-500 focus:border-danger-500 focus:ring-danger-500/20';

/** Botón primario ancho completo, mismo tratamiento que el submit de `LoginForm.tsx`. */
export const CTA_BUTTON =
  'cta-sheen mt-2 flex w-full items-center justify-between gap-3 rounded-[10px] bg-ink-850 px-[22px] py-[17px] text-[15px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-70';

/** Botón/link secundario ancho completo. */
export const GHOST_BUTTON =
  'flex w-full items-center justify-center gap-2.5 rounded-[10px] border border-[#ececec] bg-white px-[22px] py-[15px] text-[15px] font-semibold text-ink-850 transition-colors hover:border-secondary-300 hover:bg-secondary-50';

/** Marcas de registro en las esquinas de la tarjeta. */
function CornerMarks() {
  const base = 'pointer-events-none absolute h-3.5 w-3.5 border-secondary-300';
  return (
    <>
      <span aria-hidden="true" className={`${base} left-[18px] top-[18px] rounded-tl-[3px] border-l-[1.5px] border-t-[1.5px]`} />
      <span aria-hidden="true" className={`${base} right-[18px] top-[18px] rounded-tr-[3px] border-r-[1.5px] border-t-[1.5px]`} />
      <span aria-hidden="true" className={`${base} bottom-[18px] left-[18px] rounded-bl-[3px] border-b-[1.5px] border-l-[1.5px]`} />
      <span aria-hidden="true" className={`${base} bottom-[18px] right-[18px] rounded-br-[3px] border-b-[1.5px] border-r-[1.5px]`} />
    </>
  );
}

interface AuthSplitShellProps {
  /** Kicker en versalitas sobre el título de la tarjeta. */
  kicker: string;
  /** Contenido de la tarjeta: título, copy y formulario. */
  children: ReactNode;
  /**
   * Sufijo del `id` del filtro SVG del grano de papel. Los `id` de SVG son
   * globales al documento, así que cada pantalla necesita el suyo.
   */
  grainId: string;
  /** `data-testid` de la imagen del logo del tenant en el hero. */
  logoTestId?: string;
}

export function AuthSplitShell({ kicker, children, grainId, logoTestId }: AuthSplitShellProps) {
  const filtroGrano = `paper-grain-${grainId}`;

  return (
    <div className="grid min-h-screen w-full grid-cols-1 bg-white font-display lg:grid-cols-[minmax(0,1fr)_minmax(0,620px)]">
      {/* ===================== IZQUIERDA — HERO ===================== */}
      <aside className="relative order-2 flex flex-col justify-between gap-8 overflow-hidden bg-ink-950 px-6 py-10 text-white sm:px-10 lg:order-1 lg:gap-0 lg:px-[clamp(32px,4.5vw,64px)] lg:py-[clamp(32px,4vw,56px)]">
        <LoginBackgroundSlideshow />

        {/* Grano de papel sobre la foto. */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[.18] mix-blend-overlay"
        >
          <filter id={filtroGrano}>
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.35 0" />
          </filter>
          <rect width="100%" height="100%" filter={`url(#${filtroGrano})`} />
        </svg>

        <div className="relative z-[2] flex items-center gap-3.5 lg:ml-4 lg:mt-5">
          <BrandLogo markSize={72} wordmarkClassName="text-[22px]" imgTestId={logoTestId} />
        </div>

        <div className="relative z-[2] max-w-[560px]">
          <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/50 sm:mb-7 sm:text-[12px]">
            Facturación Electrónica · Panamá
          </div>
          <h1 className="m-0 text-[clamp(30px,2.7vw,48px)] font-bold leading-[1.05] tracking-[-0.035em] text-white">
            Tu facturación
            <br />
            electrónica,
            <br />
            <span className="font-medium text-white/[.78]">en regla con la DGI.</span>
          </h1>
          <p className="mt-6 max-w-[400px] text-base leading-[1.6] text-white/[.55]">
            Emite, consulta y archiva tus documentos fiscales sin salir del flujo de trabajo.
          </p>
        </div>

        <div className="relative z-[2] flex flex-col gap-1 text-[12px] text-white/[.35] sm:flex-row sm:items-center sm:justify-between sm:text-xs">
          <span>© {new Date().getFullYear()} Facturación Electrónica. Todos los derechos reservados.</span>
          <span className="font-mono tracking-[0.08em]">v{__APP_VERSION__}</span>
        </div>
      </aside>

      {/* ===================== DERECHA — TARJETA ===================== */}
      <main className="order-1 flex items-center justify-center overflow-y-auto bg-secondary-100 p-4 sm:p-6 lg:order-2 lg:p-10 short:lg:p-6">
        <div className="relative w-full max-w-[520px] rounded-3xl border border-[#f4f4f4] bg-white px-6 pb-11 pt-12 shadow-[0_1px_1px_rgba(10,34,51,.02),0_30px_60px_-30px_rgba(10,34,51,.08)] sm:px-14 sm:pt-[60px] short:pb-7 short:pt-8 short:sm:pt-9">
          <CornerMarks />

          <div className="mb-5 text-[12px] font-semibold uppercase tracking-[0.28em] text-secondary-400 short:mb-2">
            {kicker}
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}
